import { MapPinIcon } from "@heroicons/react/16/solid";
import Drawer from "@via/components/drawer.tsx";
import {
  LogoSpinner,
  OverlayLoading,
} from "@via/components/loading/loading.tsx";
import Modal from "@via/components/modal.tsx";
import { DrawerSize } from "@via/components/table/table.tsx";
import { useGlobal } from "@via/hooks/useGlobal.tsx";
import CreatePipelineModal from "@via/pages/pipelines/create-pipeline/create-pipeline-modal.tsx";
import ImportProcessFromGitModal from "@via/pages/projects/pipelines/workflow/components/import-process-from-git/import-process-from-git-modal.tsx";
import {
  fetchPipelineDetails,
  fetchProcessDetails,
  useOverwriteRevision,
  usePipelineMenuGroupsList,
  useSaveNewProcess,
  useSaveProcessRevision,
  useDuplicateProcess,
} from "@via/pages/projects/pipelines/pipelines.api.tsx";
import CreateEditProcessForm from "@via/pages/projects/pipelines/workflow/components/create-edit-process-form/create-edit-process-form.tsx";
import DeleteButtonEdge from "@via/pages/projects/pipelines/workflow/components/custom-edges.tsx";
import DeleteNodeModal from "@via/pages/projects/pipelines/workflow/components/delete-node-modal.tsx";
import EditDisplayNameModal from "@via/pages/projects/pipelines/workflow/components/edit-display-name.tsx";
import EditInputModal from "@via/pages/projects/pipelines/workflow/components/edit-input-modal.tsx";
import EditModuleModal from "@via/pages/projects/pipelines/workflow/components/edit-module-modal.tsx";
import EditOutputModal from "@via/pages/projects/pipelines/workflow/components/edit-output-modal.tsx";
import { WorkflowEditTypes } from "@via/pages/projects/pipelines/workflow/utils/constants.tsx";
import {
  addEdge,
  checkForOtherConnections,
  checkIfInputParamHasAnyConnection,
  checkIfOutputParamHasOutputNodeConnection,
  checkIsNewParameter,
  cleanAndParse,
  cleanAndParseProcess,
  cleanProcessName,
  createEmptyProcess,
  createNewIOEdges,
  createNewModuleEdges,
  createNewProcessEdges,
  formatAllPipelineData,
  formatNewIOParameterConnection,
  formatNodesDraft,
  formatPipelineProcessData,
  formatWorkflowToDraftPayload,
  generateIOTooltips,
  generateModuleTooltips,
  getDataId,
  getDataIdType,
  getIOOfProcess,
  getNodeStyle,
  getProcessesOfPipeline,
  packProcessToOverwrite,
  packProcessToSave,
  processNewModuleEdges,
  removeEmptyStrings,
  SaveProcessMode,
  useUpdateNodeInternalsAsync,
} from "@via/pages/projects/pipelines/workflow/utils/helpers.tsx";
import { getMainPipeline } from "@via/pages/projects/runs/utils/helpers.tsx";
import Config from "@via/utils/config.tsx";
import { NODE_TYPES } from "@via/utils/constants.tsx";
import {
  EdgeInterface,
  NodeInterface,
  PipelineInterface,
  ProcessNodeInterface,
  SelectOption,
} from "@via/utils/interfaces.tsx";
import {
  Background,
  BackgroundVariant,
  type Connection,
  ConnectionLineType,
  Controls,
  type Edge,
  type EdgeProps,
  MiniMap,
  type NodeProps,
  ReactFlow,
  useEdgesState,
  useNodesState,
  useReactFlow,
  useStoreApi,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { MenuGroupListItem, MenuGroupType } from "common";
import {
  type JSX,
  memo,
  type MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import InputNode from "@via/pages/projects/pipelines/workflow/components/input-node.tsx";
import ModuleNode from "@via/pages/projects/pipelines/workflow/components/module-node.tsx";
import OutputNode from "@via/pages/projects/pipelines/workflow/components/output-node.tsx";
import Sidebar from "@via/pages/projects/pipelines/workflow/components/pipeline-sidebar.tsx";
import ProcessNode from "@via/pages/projects/pipelines/workflow/components/process-node.tsx";
import ModelNode from "@via/pages/projects/pipelines/workflow/components/model-node.tsx";
import SkillNode from "@via/pages/projects/pipelines/workflow/components/skill-node.tsx";
import "./pipeline-graph.css";
import { useChangesDetect } from "@via/hooks/useChangesDetect";
import { useFetchAIModelDetails } from "@via/pages/cortex/model-garden/model-garden.api";
import { useCortexContext } from "@via/context/cortex-context";

const edgeTypes = {
  deleteButtonEdge: DeleteButtonEdge,
};
const defaultEdgeOptions = {
  type: "deleteButtonEdge",
};

const proOptions = { hideAttribution: true };
const shouldUpdateDraft = (instance, modal, drawer) => {
  return instance && !drawer && !modal;
};

const fitViewOptions = {
  minZoom: 0.2,
  maxZoom: 0.7,
};

const MemoizedSidebar = memo(Sidebar);

export default function PipelineGraph({
  builderOptions,
  parameters,
}): JSX.Element {
  const store = useStoreApi();
  const ref = useRef<HTMLInputElement>(null);
  // this ref is used to check if a node is being started to be dragged
  const isNodeDragStarted = useRef(false);
  // this ref is used to sync the pipelineDraft with the actual edges and prevent infinite loops
  const hasSetPipelineDraft = useRef(false);
  const [isFetchProcessDetailsLoading, setIsFetchProcessDetailsLoading] =
    useState(false);
  const { getEdges, fitView } = useReactFlow();
  const [openDrawer, setOpenDrawer] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [drawerContent, setDrawerContent] = useState<string | null>(null);
  const [modalContent, setModalContent] = useState<string | null>(null);
  const [contentData, setContentData] = useState<any>({});
  const [isCreatePipelineModalOpen, setIsCreatePipelineModalOpen] =
    useState(false);
  const [isImportProcessFromGitModalOpen, setIsImportProcessFromGitModalOpen] =
    useState(false);
  const [menuGroups, setMenuGroups] = useState<SelectOption[]>([]);
  const { data: menuGroupsListData, isSuccess: isMenuGroupsListSuccess } =
    usePipelineMenuGroupsList();
  const [
    isDeleteNodeConfirmationModalOpen,
    setIsDeleteNodeConfirmationModalOpen,
  ] = useState(false);
  const [modulePipelineId, setModulePipelineId] = useState(null);
  const [selectedNode, setSelectedNode] = useState<NodeInterface | null>();
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [isLoading, setIsLoading] = useState(true);
  const updateNodeInternals = useUpdateNodeInternalsAsync();
  const [rfInstance, setRfInstance] = useState<any>(null);
  const [showMiniMap, setShowMiniMap] = useState(true);
  const [focusedNodeId, setFocusedNodeId] = useState("");
  const [shouldCloseDrawer, setShouldCloseDrawer] = useState(true);
  const {
    gNum,
    setGNum,
    mainPipeline,
    selectedPipeline,
    pipelineDraft,
    setPipelineDraft,
    setSelectedPipeline,
  } = useGlobal();
  const { increaseChangeCount } = useChangesDetect();
  const {
    isDrawerOpen: isCortexDrawerOpen,
    setShouldUsePortalForCortexDrawer,
  } = useCortexContext();
  const {
    mutate: createProcessRevision,
    data: createProcessRevisionData,
    isPending: isCreateProcessRevisionLoading,
    isSuccess: isCreateProcessRevisionSuccess,
  } = useSaveProcessRevision();
  const { mutate: duplicateProcess } = useDuplicateProcess();
  const { mutateAsync: fetchAIModelDetailsMutation } = useFetchAIModelDetails();

  const {
    mutate: overwriteRevision,
    data: overwriteRevisionData,
    isPending: isOverwriteRevisionLoading,
    isSuccess: isOverwriteRevisionSuccess,
  } = useOverwriteRevision();

  const {
    mutate: createNewProcess,
    data: createNewProcessData,
    isPending: isCreateNewProcessLoading,
    isSuccess: isCreateNewProcessSuccess,
  } = useSaveNewProcess();

  useEffect(() => {
    // Render an empty pipeline for now if no data is returned
    if (!mainPipeline || !parameters || !pipelineDraft) {
      setNodes([]);
      setEdges([]);
      setIsLoading(false);
      return;
    }

    const result = formatAllPipelineData(
      pipelineDraft,
      selectedPipeline,
      parameters
    );

    const { nodesFormatted = [], edgesFormatted = [] } = result ?? {
      nodesFormatted: [],
      edgesFormatted: [],
    };
    if (nodesFormatted.length) {
      setNodes(nodesFormatted);
      // Update node internals for each node to fix the invisible node bug
      for (const node of nodesFormatted) {
        updateNodeInternals(node.id);
      }
    }

    setEdges(edgesFormatted || []);
    setIsLoading(false);

    // this syncs the pipelineDraft edges with the actual edges
    if (!hasSetPipelineDraft?.current) {
      setPipelineDraft(prevDraft => ({
        ...prevDraft,
        edges: edgesFormatted.map(edge => edge.id),
      }));
      hasSetPipelineDraft.current = true;
    }
  }, [mainPipeline, pipelineDraft, parameters]);

  useEffect(() => {
    if (menuGroupsListData) {
      const groups = menuGroupsListData.data.map((mg: MenuGroupListItem) => {
        return { label: mg.name, value: mg.id };
      });
      setMenuGroups(groups);
    }
  }, [isMenuGroupsListSuccess, menuGroupsListData]);

  useEffect(() => {
    if (shouldUpdateDraft(rfInstance, openModal, openDrawer)) {
      increaseChangeCount();
      const flow = rfInstance?.toObject();
      const result = formatWorkflowToDraftPayload({ ...pipelineDraft }, flow);
      // set it false, so that we can update the pipelineDraft again when the flow changes
      hasSetPipelineDraft.current = false;
      setPipelineDraft(result);
    }
  }, [edges.length, nodes.length, openModal, openDrawer]);

  // Capture latest on unmount
  useEffect(() => {
    return () => {
      if (rfInstance) {
        const flow = rfInstance?.toObject();
        const result = formatWorkflowToDraftPayload({ ...pipelineDraft }, flow);
        setPipelineDraft(result);
      }
    };
  }, [rfInstance]);

  // Focus on a node in viewport
  useEffect(() => {
    if (focusedNodeId) {
      setTimeout(() => {
        fitView({
          nodes: [{ id: focusedNodeId }],
          ...fitViewOptions,
        });
      }, 0);
      setFocusedNodeId("");
    }
  }, [fitView, focusedNodeId]);

  const onEdit = useCallback(
    async (action, data) => {
      if (action === WorkflowEditTypes.EditProcess) {
        const { processId } = data;
        setOpenModal(false); // Close the modal before we open the drawer to edit a Process
        setIsFetchProcessDetailsLoading(true);
        const processDetailsData = await fetchProcessDetails(processId);
        const parsed = cleanAndParseProcess(processDetailsData);
        parsed.type = MenuGroupType.PROCESS;
        setContentData(parsed);
        setDrawerContent(WorkflowEditTypes.EditProcess);
        setOpenDrawer(true);
        setIsFetchProcessDetailsLoading(false);
      }
      if (action === WorkflowEditTypes.EditModel) {
        const { modelId } = data;
        setOpenModal(false);
        setIsFetchProcessDetailsLoading(true);
        const aiModelDetails = await fetchAIModelDetailsMutation(modelId);
        const parsed = cleanAndParseProcess(aiModelDetails.process);
        parsed.type = MenuGroupType.AI_MODEL;
        parsed.modelData = aiModelDetails;
        setContentData(parsed);
        setDrawerContent(WorkflowEditTypes.EditProcess);
        setOpenDrawer(true);
        setIsFetchProcessDetailsLoading(false);
      }
      if (action === WorkflowEditTypes.EditSkill) {
        const { skillId } = data;
        setOpenModal(false);
        setIsFetchProcessDetailsLoading(true);
        const processDetailsData = await fetchProcessDetails(skillId);
        const parsed = cleanAndParseProcess(processDetailsData);
        parsed.type = MenuGroupType.MODEL_SKILL;
        setContentData(parsed);
        setDrawerContent(WorkflowEditTypes.EditProcess);
        setOpenDrawer(true);
        setIsFetchProcessDetailsLoading(false);
      }
      if (action === WorkflowEditTypes.EditModule) {
        const { pipelineId } = data;
        setModulePipelineId(pipelineId);
        fetchPipelineDetails(pipelineId).then(data => {
          // We have to remove escaped characters
          // const parsed = cleanAndParseProcess(data);
          const parsed = formatPipelineProcessData(data?.pipeline);
          setContentData(parsed);
          setModalContent(WorkflowEditTypes.EditModule);
          setOpenModal(true);
        });
      }
      if (action === WorkflowEditTypes.EditInput) {
        setContentData(data);
        setModalContent(WorkflowEditTypes.EditInput);
        setOpenModal(true);
      }
      if (action === WorkflowEditTypes.EditOutput) {
        setContentData(data);
        setModalContent(WorkflowEditTypes.EditOutput);
        setOpenModal(true);
      }
    },
    [openModal, openDrawer, contentData, selectedPipeline]
  );

  const onEditDisplayName = useCallback(
    data => {
      setContentData(data);
      setModalContent(WorkflowEditTypes.EditDisplayName);
      setOpenModal(true);
    },
    [openModal, openDrawer, contentData, pipelineDraft]
  );

  const onModuleOpen = useCallback(id => {
    window.open(`${Config.HOST}/pipelines/${id}`, "_blank", "noreferrer");
  }, []);

  const onConnectStart = useCallback(() => {
    setNodes((ns): any =>
      ns.map((n: NodeProps) => {
        return {
          ...n,
          data: { ...n?.data, isDragging: true },
        };
      })
    );
  }, []);

  const onConnectEnd = useCallback(() => {
    setNodes((ns): any =>
      ns.map((n: NodeProps) => {
        return {
          ...n,
          data: {
            ...n?.data,
            isDragging: false,
            hovered: null,
            hoverType: null,
            hoverHandle: null,
            hoverNode: null,
            hoverNodeType: null,
            hoverNodeConnected: null,
          },
        };
      })
    );
  }, []);

  const onHandleHover = (
    hovered,
    type,
    handleId,
    nodeId,
    hoverNodeType,
    hoverNodeConnected
  ) => {
    // If a node is being dragged, don't highlight connected nodes
    if (isNodeDragStarted?.current) {
      return;
    }
    setNodes((ns): any =>
      ns.map((n: NodeProps) => {
        if (n.data.isDragging) {
          return n;
        }
        return {
          ...n,
          data: {
            ...n?.data,
            hovered,
            hoverHandleType: type,
            hoverHandle: handleId,
            hoverNode: nodeId,
            hoverNodeType,
            hoverNodeConnected,
          },
          zIndex: n.id === nodeId ? 1300 : 0,
        };
      })
    );
    // Find and highlight the only hovered edge
    setEdges((currentEdges: any) => {
      return currentEdges.map((edge: Edge) => {
        if (edge.targetHandle === handleId || edge.sourceHandle === handleId) {
          return {
            ...edge,
            style: {
              strokeWidth: 3,
            },
          };
        } else {
          return {
            ...edge,
            style: {
              ...edge.style,
              opacity: 0.2,
            },
          };
        }
      });
    });
  };

  const onHandleLeave = useCallback(() => {
    // If a node is being dragged, don't highlight connected nodes
    if (isNodeDragStarted?.current) {
      return;
    }
    setNodes((ns): any =>
      ns.map((n: NodeProps) => {
        if (n?.data?.isDragging) {
          return n;
        } else {
          return {
            ...n,
            data: {
              ...n?.data,
              hovered: null,
              hoverType: null,
              hoverHandle: null,
              hoverNode: null,
              hoverNodeType: null,
              hoverNodeConnected: null,
            },
          };
        }
      })
    );
  }, []);

  const onDelete = id => {
    const edges = getEdges();
    const deletingEdges = edges.filter(
      edge => edge.target === id || edge.source === id
    );
    setNodes((nodes): any =>
      nodes
        .map((node: any) => {
          const match = deletingEdges.find(
            edge => edge.target === node.id || edge.source === node.id
          );
          if (node.id === id) {
            return null;
          }

          if (!match) {
            return node;
          }

          if (node.type === NODE_TYPES.InputNode) {
            const hasOtherConnections = checkForOtherConnections(
              edges,
              match?.id
            );
            const outputHandle = hasOtherConnections
              ? node?.data?.outputHandles?.[0]
              : `o-inPro-1-inPara-${node.id}`;
            const outputTooltip = hasOtherConnections
              ? node?.data?.tooltips?.outputs?.[0]
              : { connect: "Connect to Input" };
            return {
              ...node,
              data: {
                ...node.data,
                inputHandles: [],
                outputHandles: [outputHandle],
                tooltips: {
                  inputs: [],
                  outputs: [outputTooltip],
                },
              },
            };
          }

          if (node.type === NODE_TYPES.OutputNode) {
            return {
              ...node,
              data: {
                ...node.data,
                inputHandles: [`i-outPro-1-outPara-${node.id}`],
                outputHandles: [],
                tooltips: {
                  inputs: [{ connect: "Connect to Output" }],
                  outputs: [],
                },
              },
            };
          }
          return node;
        })
        .filter(node => node !== null)
    ); // Filter out null values

    updateNodeInternals(id);
    setSelectedNode(null);

    setEdges((edges): any =>
      edges
        .filter((edge: EdgeProps) => edge.target !== id)
        .filter((edge: EdgeProps) => edge.source !== id)
    );

    if (selectedNode?.type === NODE_TYPES.ProcessNode) {
      const processId = selectedNode?.data?.processId;
      const updatedSelectedPipeline = {
        ...selectedPipeline,
      } as PipelineInterface;
      delete updatedSelectedPipeline[`process_${processId}`];
      setSelectedPipeline(updatedSelectedPipeline);
    }
  };

  const saveInputChange = changes => {
    if (selectedNode) {
      const { id } = selectedNode as NodeInterface;
      const { label, showSett, ...rest } = changes;
      const shouldIncludeShowSett = showSett !== undefined && showSett !== null;
      const cleaned = removeEmptyStrings(rest);
      const settingsToUpdate = shouldIncludeShowSett
        ? { ...cleaned, showSett }
        : { ...cleaned };
      setNodes((ns): any =>
        ns.map((n: NodeProps) => {
          if (n.id === id) {
            return {
              ...n,
              data: { ...n.data, label, settings: { ...settingsToUpdate } },
            };
          }
          return n;
        })
      );
    }
    setOpenModal(false);
  };

  const saveOutputChange = changes => {
    if (selectedNode) {
      const { id } = selectedNode as NodeInterface;
      const { label, ...rest } = changes;
      setNodes((ns): any =>
        ns.map((n: NodeProps) => {
          if (n.id === id) {
            return {
              ...n,
              data: { ...n.data, label, settings: { ...rest } },
            };
          }
          return n;
        })
      );
    }
    setOpenModal(false);
  };

  const saveDisplayName = changes => {
    if (selectedNode) {
      const { id } = selectedNode as NodeInterface;
      setNodes((ns): any =>
        ns.map((n: NodeProps) => {
          if (n.id === id) {
            return {
              ...n,
              data: { ...n.data, label: changes?.label },
            };
          }
          return n;
        })
      );
    }
    setOpenModal(false);
  };

  const handleOverwriteProcess = (processDraft, saveMode) => {
    if (saveMode === SaveProcessMode.SAVE) {
      setShouldCloseDrawer(false);
    } else {
      setShouldCloseDrawer(true);
    }
    const payload = packProcessToOverwrite(processDraft);
    overwriteRevision({ processId: processDraft?.processId, payload });
  };

  const handleSaveProcess = processDraft => {
    const payload = packProcessToSave(processDraft);
    if (processDraft?.isNewProcess) {
      createNewProcess(payload);
      return;
    }
    createProcessRevision({ processId: processDraft?.processId, payload });
  };

  const handleDuplicateProcess = processId => {
    duplicateProcess(processId);
    setOpenDrawer(false);
  };

  useEffect(() => {
    if (overwriteRevisionData) {
      const parsed = cleanAndParseProcess(overwriteRevisionData);
      setContentData(parsed);
      handleChangeRevision(parsed);
    }
  }, [isOverwriteRevisionSuccess]);

  useEffect(() => {
    if (createProcessRevisionData) {
      const parsed = cleanAndParseProcess(createProcessRevisionData);
      setContentData(parsed);
      handleChangeRevision(parsed);
    }
  }, [isCreateProcessRevisionSuccess]);

  useEffect(() => {
    if (createNewProcessData) {
      const newGNum = gNum + 1;
      // Calculate the new position based on existing nodes
      const existingNodePositions =
        nodes.map((node: any) => node.position.y) || [];
      const newYPosition =
        existingNodePositions.length > 0
          ? Math.max(...existingNodePositions) + 100 // Place below the highest existing node
          : 50; // Default starting position if no nodes exist

      const position = { x: 50, y: newYPosition };
      handleCreateProcessNodeRender(createNewProcessData, position, newGNum);
      setFocusedNodeId(newGNum.toString());
      setOpenDrawer(false);
    }
  }, [isCreateNewProcessSuccess]);

  // While Process Detail drawer is open, force Cortex into overlay/portal mode so it can be opened.
  // Keep overlay mode on if Cortex is currently open (prevents janky switching mid-session).
  useEffect(() => {
    if (openDrawer) {
      setShouldUsePortalForCortexDrawer(true);
      return;
    }
    if (!isCortexDrawerOpen) {
      setShouldUsePortalForCortexDrawer(false);
    }
  }, [openDrawer, isCortexDrawerOpen, setShouldUsePortalForCortexDrawer]);

  useEffect(() => {
    return () => {
      setShouldUsePortalForCortexDrawer(false);
    };
  }, [setShouldUsePortalForCortexDrawer]);

  const onCancelDrawer = () => {
    setOpenDrawer(false);
    // Wait until the drawer is closed, then setContent to null to preserve animation
    setTimeout(() => setDrawerContent(null), 1000);
  };

  const onCancelModal = () => {
    setOpenModal(false);
  };

  const handleNodeDragStart = useCallback(() => {
    isNodeDragStarted.current = true;
  }, []);

  // Always capture the latest position of a node onNodeDragStop
  const handleNodeDragStop = useCallback((_, node) => {
    isNodeDragStarted.current = false;

    if (pipelineDraft?.nodes) {
      const selected = `g-${node.id}`;
      setPipelineDraft(prevData => ({
        ...prevData,
        nodes: {
          ...prevData.nodes,
          [selected]: {
            ...prevData.nodes[selected],
            xCoordinate: node.position.x,
            yCoordinate: node.position.y,
          },
        },
      }));
    }
  }, []);

  // Leave these log statements for now, setting up for modal -> editing processes
  const handleNodeClick = (_, node): void => {
    const { id, data } = node;
    setSelectedNode(node);
    // Find and highlight our connectable handles recursively
    const mappedEdges: any = edges.map((edge: Edge) => {
      if (edge.source === id || edge.target === id) {
        return {
          ...edge,
          style: {
            strokeWidth: 3,
          },
        };
      } else {
        return {
          ...edge,
          style: {
            opacity: 0.2,
          },
        };
      }
    });
    setEdges(mappedEdges);
    if (node.type === NODE_TYPES.ModuleNode) {
      setModulePipelineId(node?.data?.pipelineId);
      console.log(data);
    }
    if (node.type === NODE_TYPES.InputNode) {
      console.log(data);
    }
    if (node.type === NODE_TYPES.OutputNode) {
      console.log(data);
    }
    if (node.type === NODE_TYPES.ProcessNode) {
      console.log(data);
    }
    if (node.type === NODE_TYPES.ModelNode) {
      console.log(data);
    }
    if (node.type === NODE_TYPES.SkillNode) {
      console.log(data);
    }
  };

  const onConnect = async (newConnection: Connection): Promise<void> => {
    let match = "";

    const inputParamHasAnyConnection = checkIfInputParamHasAnyConnection(
      edges,
      newConnection
    );
    if (inputParamHasAnyConnection) {
      return;
    }
    const outputParamHasOutputNodeConnection =
      checkIfOutputParamHasOutputNodeConnection(edges, newConnection);
    if (outputParamHasOutputNodeConnection) {
      return;
    }

    const isNewParameter = checkIsNewParameter(newConnection);
    const formatted = isNewParameter
      ? formatNewIOParameterConnection(newConnection)
      : newConnection;
    const { source, target, sourceHandle, targetHandle } = formatted;
    setNodes((ns): any =>
      ns.map((node: any) => {
        if (
          (node.type === NODE_TYPES.InputNode ||
            node.type === NODE_TYPES.OutputNode) &&
          (source === node.id || target === node.id)
        ) {
          if (source === node.id) {
            match = source;
            const outputTooltips = generateIOTooltips(
              node?.name,
              [sourceHandle as string],
              parameters
            );
            return {
              ...node,
              data: {
                ...node.data,
                outputHandles: [sourceHandle],
                tooltips: {
                  inputs: [],
                  outputs: outputTooltips,
                },
              },
            };
          }
          if (target === node.id) {
            match = target;
            const inputTooltips = generateIOTooltips(
              node?.name,
              [targetHandle as string],
              parameters
            );
            return {
              ...node,
              data: {
                ...node.data,
                inputHandles: [targetHandle],
                tooltips: {
                  inputs: inputTooltips,
                  outputs: [],
                },
              },
            };
          }
        }
        return node;
      })
    );

    if (match) {
      await updateNodeInternals(match);
    }
    setEdges(oldEdges => addEdge(formatted, oldEdges));
  };

  const onNodeMouseEnter = useCallback(
    (_: MouseEvent, node: any) => {
      // If a node is being dragged, don't highlight connected nodes
      if (isNodeDragStarted?.current) {
        return;
      }
      // Find and highlight our connected nodes recursively
      let isDragging = false;
      let hasChange = false;
      const id = node?.id;
      const { edges } = store.getState();
      setNodes((ns): any =>
        ns.map((n: NodeProps) => {
          if (selectedNode?.id === n?.id) {
            return n;
          }
          if (n.data?.isDragging) {
            isDragging = true;
            return n;
          }
          return {
            ...n,
            zIndex: node.id === n.id ? 1300 : 0,
          };
        })
      );
      // Find and highlight our connectable handles recursively
      const mappedEdges: any = edges.map((edge: Edge) => {
        if (selectedNode) {
          return edge;
        }
        if (isDragging) {
          return edge;
        }
        if (edge.source === id || edge.target === id) {
          hasChange = true;
          return {
            ...edge,
            style: {
              strokeWidth: 3,
            },
          };
        } else {
          return {
            ...edge,
            style: {
              opacity: 0.2,
            },
          };
        }
      });

      if (hasChange) {
        setEdges(mappedEdges);
      }
    },
    [selectedNode?.id]
  );

  const onNodeMouseLeave = useCallback(
    (_: MouseEvent) => {
      // If a node is being dragged, don't highlight connected nodes
      if (isNodeDragStarted?.current) {
        return;
      }
      let isDragging = false;
      setNodes((ns): any =>
        ns.map((node: NodeProps) => {
          if (selectedNode) {
            return node;
          }
          if (node.data?.isDragging) {
            isDragging = true;
            return node;
          }
          return {
            ...node,
            zIndex: 0,
            className: "",
          };
        })
      );
      setEdges((edges): any =>
        edges.map((edge: EdgeProps) => {
          if (selectedNode) {
            return edge;
          }
          if (isDragging) {
            return edge;
          }
          return {
            ...edge,
            style: {
              strokeWidth: 1,
            },
          };
        })
      );
    },
    [selectedNode?.id]
  );

  const onDragOver = useCallback(event => {
    if (pipelineDraft?.isEditingAllowed) {
      event.preventDefault();
      event.dataTransfer.dropEffect = "move";
    }
  }, []);

  // Utility to create a node
  const createNode = (
    type: any,
    id: string,
    position: any,
    data: { name: string; id: string },
    inputHandles: string[],
    outputHandles: string[],
    inputTooltips: any = [],
    outputTooltips: any = []
  ): any => {
    const dataIdType = getDataIdType(type);
    const dataId = getDataId(data?.id?.toString()) ?? dataIdType;
    const dataName =
      type === NODE_TYPES.ModuleNode
        ? cleanProcessName(data?.name || "")
        : data?.name;

    return {
      id,
      name: dataName,
      type,
      data: {
        label: dataName,
        type,
        className: getNodeStyle(type),
        settings: {},
        [dataIdType]: dataId,
        inputHandles,
        outputHandles,
        tooltips: {
          inputs: inputTooltips,
          outputs: outputTooltips,
        },
      },
      position,
    };
  };

  // Utility to update state
  const updateState = (newGNum, node): void => {
    console.log(newGNum);
    setGNum(newGNum);
    console.log(node);
    setNodes(nds => nds.concat(node));
  };

  // Handling different types of nodes on drag and drop
  const handleInputOutputNodeDnD = (type, position, newGNum): void => {
    const gNumString = newGNum?.toString();
    const handles = createNewIOEdges(type, gNumString);
    const input = type === NODE_TYPES.InputNode;
    const data = {
      name: input ? "inputParam" : "outputParam",
      id: input ? "inPro" : "outPro",
    };
    const inputTooltips = input ? [] : [{ connect: "Connect to Output" }];
    const outputTooltips = input ? [{ connect: "Connect to Input" }] : [];
    const node = createNode(
      type,
      gNumString,
      position,
      data,
      handles?.inputHandles,
      handles?.outputHandles,
      inputTooltips,
      outputTooltips
    );
    updateState(newGNum, node);
  };

  // @@TODO [CLEANUP] Consolidate the edge, handle and target creation with the code in the helpers file
  const handleModuleNodeDnD = (id, position, newGNum): void => {
    const gNumString = newGNum?.toString();
    fetchPipelineDetails(id).then(data => {
      const pipeline = getMainPipeline(data?.pipeline);
      const parsedEdges = cleanAndParse(pipeline?.edges);
      const parsedNodes = cleanAndParse(pipeline?.nodes);
      const { edges: moduleEdges, matchingNodes } = processNewModuleEdges(
        parsedEdges,
        gNumString,
        id
      );
      const formattedEdges = createNewModuleEdges(moduleEdges, gNumString);
      const formattedNodes = formatNodesDraft(parsedNodes);
      const processes =
        getProcessesOfPipeline(data?.pipeline, matchingNodes, formattedNodes) ??
        [];
      const io = getIOOfProcess(data?.pipeline) ?? [];
      const inputIO = io.filter(i => i.parameterType === "inputs");
      const outputIO = io.filter(i => i.parameterType === "outputs");
      const inputHandles =
        formattedEdges
          .filter(edge => edge.id.startsWith("pi"))
          .map(edge => edge.id) || [];

      const outputHandles =
        formattedEdges
          .filter(edge => edge.id.startsWith("po"))
          .map(edge => edge.id) || [];
      const inputTooltips = generateModuleTooltips(
        inputHandles,
        processes,
        inputIO,
        matchingNodes
      );
      const outputTooltips = generateModuleTooltips(
        outputHandles,
        processes,
        outputIO,
        matchingNodes
      );
      const metadata = { name: pipeline?.name, id: pipeline?.id };
      const node = createNode(
        NODE_TYPES.ModuleNode,
        gNumString,
        position,
        metadata,
        inputHandles,
        outputHandles,
        inputTooltips,
        outputTooltips
      );
      updateState(newGNum, node);
    });
  };

  const handleProcessNodeDnD = async (id, position, newGNum): Promise<void> => {
    const gNumString = newGNum?.toString();
    setIsFetchProcessDetailsLoading(true);
    const data = await fetchProcessDetails(id);
    const inputs = data?.inputs || [];
    const outputs = data?.outputs || [];
    const formattedEdges = createNewProcessEdges(data, gNumString);
    const inputHandles = formattedEdges
      .filter(edge => edge.id.startsWith("i"))
      .map(edge => edge.id);

    const outputHandles = formattedEdges
      .filter(edge => edge.id.startsWith("o"))
      .map(edge => edge.id);
    const inputTooltips = inputs.map(
      ({ name, sname, qualifier, file_type }) => ({
        identifier: name,
        name: sname,
        qualifier,
        fileType: file_type,
      })
    );
    const outputTooltips = outputs.map(
      ({ name, sname, qualifier, file_type }) => ({
        identifier: name,
        name: sname,
        qualifier: qualifier,
        fileType: file_type,
      })
    );
    const metadata = { name: data?.name, id: data?.id };
    const node = createNode(
      NODE_TYPES.ProcessNode,
      gNumString,
      position,
      metadata,
      inputHandles,
      outputHandles,
      inputTooltips,
      outputTooltips
    );
    updateState(newGNum, node);
    setIsFetchProcessDetailsLoading(false);
    const updatedSelectedPipeline = {
      ...selectedPipeline,
      [`process_${data.id}`]: data,
    } as PipelineInterface;
    setSelectedPipeline(updatedSelectedPipeline);
  };

  const handleModelNodeDnD = async (id, position, newGNum): Promise<void> => {
    const gNumString = newGNum?.toString();
    setIsFetchProcessDetailsLoading(true);
    const data = await fetchProcessDetails(id);
    const inputs = data?.inputs || [];
    const outputs = data?.outputs || [];
    const formattedEdges = createNewProcessEdges(data, gNumString);
    const inputHandles = formattedEdges
      .filter(edge => edge.id.startsWith("i"))
      .map(edge => edge.id);

    const outputHandles = formattedEdges
      .filter(edge => edge.id.startsWith("o"))
      .map(edge => edge.id);
    const inputTooltips = inputs.map(
      ({ name, sname, qualifier, file_type }) => ({
        identifier: name,
        name: sname,
        qualifier,
        fileType: file_type,
      })
    );
    const outputTooltips = outputs.map(
      ({ name, sname, qualifier, file_type }) => ({
        identifier: name,
        name: sname,
        qualifier: qualifier,
        fileType: file_type,
      })
    );
    const metadata = { name: data?.name, id: data?.id };
    const node = createNode(
      NODE_TYPES.ModelNode,
      gNumString,
      position,
      metadata,
      inputHandles,
      outputHandles,
      inputTooltips,
      outputTooltips
    );
    updateState(newGNum, node);
    setIsFetchProcessDetailsLoading(false);
  };

  const handleSkillNodeDnD = async (id, position, newGNum): Promise<void> => {
    const gNumString = newGNum?.toString();
    setIsFetchProcessDetailsLoading(true);
    const data = await fetchProcessDetails(id);
    const inputs = data?.inputs || [];
    const outputs = data?.outputs || [];
    const formattedEdges = createNewProcessEdges(data, gNumString);
    const inputHandles = formattedEdges
      .filter(edge => edge.id.startsWith("i"))
      .map(edge => edge.id);

    const outputHandles = formattedEdges
      .filter(edge => edge.id.startsWith("o"))
      .map(edge => edge.id);
    const inputTooltips = inputs.map(
      ({ name, sname, qualifier, file_type }) => ({
        identifier: name,
        name: sname,
        qualifier,
        fileType: file_type,
      })
    );
    const outputTooltips = outputs.map(
      ({ name, sname, qualifier, file_type }) => ({
        identifier: name,
        name: sname,
        qualifier: qualifier,
        fileType: file_type,
      })
    );
    const metadata = { name: data?.name, id: data?.id };
    const node = createNode(
      NODE_TYPES.SkillNode,
      gNumString,
      position,
      metadata,
      inputHandles,
      outputHandles,
      inputTooltips,
      outputTooltips
    );
    updateState(newGNum, node);
    setIsFetchProcessDetailsLoading(false);
  };

  const handleChangePipelineRevisionInModule = id => {
    const gNum = (selectedNode as NodeInterface)?.id;
    const oldInputTooltips = selectedNode?.data?.tooltips?.inputs || [];
    const oldOutputTooltips = selectedNode?.data?.tooltips?.outputs || [];

    setSelectedPipeline({
      ...selectedPipeline,
      [`oldModuleData`]: {},
      [`currentModuleData`]: {},
    } as PipelineInterface);
    const oldModuleName = (selectedNode as NodeInterface)?.name;

    fetchPipelineDetails(id).then(data => {
      const pipeline = getMainPipeline(data?.pipeline);
      const parsedEdges = cleanAndParse(pipeline?.edges);
      const parsedNodes = cleanAndParse(pipeline?.nodes);
      const { edges: moduleEdges, matchingNodes } = processNewModuleEdges(
        parsedEdges,
        gNum,
        id
      );
      const newEdges = createNewModuleEdges(moduleEdges, gNum);
      const formattedNodes = formatNodesDraft(parsedNodes);
      const processes =
        getProcessesOfPipeline(data?.pipeline, matchingNodes, formattedNodes) ??
        [];
      const io = getIOOfProcess(data?.pipeline) ?? [];
      const inputIO = io.filter(i => i.parameterType === "inputs");
      const outputIO = io.filter(i => i.parameterType === "outputs");
      const inputHandles =
        newEdges
          .filter(edge => edge.id.startsWith("pi"))
          .map(edge => edge.id) || [];
      const outputHandles =
        newEdges
          .filter(edge => edge.id.startsWith("po"))
          .map(edge => edge.id) || [];
      const inputTooltips = generateModuleTooltips(
        inputHandles,
        processes,
        inputIO,
        matchingNodes
      );
      const outputTooltips = generateModuleTooltips(
        outputHandles,
        processes,
        outputIO,
        matchingNodes
      );

      setSelectedPipeline({
        ...selectedPipeline,
        [`currentModuleData`]: {
          inputTooltips,
          outputTooltips,
        },
        [`oldModuleData`]: {
          oldInputTooltips,
          oldOutputTooltips,
        },
      } as PipelineInterface);
      const metadata = { name: oldModuleName, id: pipeline?.id };
      const node = createNode(
        NODE_TYPES.ModuleNode,
        gNum,
        (selectedNode as NodeInterface)?.position,
        metadata,
        inputHandles,
        outputHandles,
        inputTooltips,
        outputTooltips
      );
      updateState(gNum, node);

      setOpenModal(false);
    });
  };

  // Fetch AI model details when revision changes (only for AI model type)
  const handleRevisionFetch = useCallback(
    async (processId: number) => {
      if (contentData?.type === MenuGroupType.AI_MODEL) {
        const aiModelDetails = await fetchAIModelDetailsMutation(
          processId.toString()
        );
        return aiModelDetails;
      }
      return null;
    },
    [contentData?.type, fetchAIModelDetailsMutation]
  );

  const handleChangeRevision = (
    revision,
    { closeDrawer = shouldCloseDrawer } = {}
  ) => {
    if (!selectedNode) {
      if (closeDrawer) {
        setOpenDrawer(false);
      }
      return;
    }
    // save the old process tooltips
    const oldProcessInputTooltips =
      (selectedNode as ProcessNodeInterface)?.data?.tooltips?.inputs || [];
    const oldProcessOutputTooltips =
      (selectedNode as ProcessNodeInterface)?.data?.tooltips?.outputs || [];
    let selectedNodeNewProcessEdges: EdgeInterface[] | object[] = [];
    let otherNodesWithSameProcessId: string[] = [];
    let otherNodesNewProcessEdges: EdgeInterface[] = [];
    const { id } = selectedNode;
    selectedNodeNewProcessEdges = createNewProcessEdges(revision, id);

    const { processId } = (selectedNode as ProcessNodeInterface).data;

    otherNodesWithSameProcessId = nodes
      .filter(
        (node: NodeInterface) =>
          node.type === NODE_TYPES.ProcessNode &&
          node.data?.processId == Number(processId) &&
          node.id !== id
      )
      .map((node: NodeInterface) => node?.id);

    otherNodesNewProcessEdges = otherNodesWithSameProcessId.reduce<
      EdgeInterface[]
    >((edges, gNum) => edges.concat(createNewProcessEdges(revision, gNum)), []);

    const getHandles = (type, nodeId, filterNodes) =>
      filterNodes
        .filter(
          edge => (type === "input" ? edge.target : edge.source) === nodeId
        )
        .map(edge =>
          type === "input" ? edge.targetHandle : edge.sourceHandle
        );

    const getTooltips = (type, nodeId, filterNodes) =>
      filterNodes
        .filter(
          edge => (type === "input" ? edge.target : edge.source) === nodeId
        )
        .map(item => ({
          identifier: item?.data?.name,
          name: item?.data?.sname || item?.data?.name,
          qualifier: item?.data?.qualifier,
          fileType: item?.data?.file_type,
        }));

    const selectedNodeInputTooltips = getTooltips(
      "input",
      id,
      selectedNodeNewProcessEdges
    );
    const selectedNodeOutputTooltips = getTooltips(
      "output",
      id,
      selectedNodeNewProcessEdges
    );

    const updatedSelectedPipeline = {
      ...selectedPipeline,
      [`process_${revision.id}`]: revision,
      changedProcessData: {
        [`tooltipsDiff_${selectedNode.id}`]: {
          oldInputTooltips: oldProcessInputTooltips,
          oldOutputTooltips: oldProcessOutputTooltips,
          newInputTooltips: selectedNodeInputTooltips,
          newOutputTooltips: selectedNodeOutputTooltips,
        },
      },
    } as PipelineInterface;
    if (contentData?.id !== revision?.id) {
      delete updatedSelectedPipeline[`process_${contentData?.id}`];
    }
    setSelectedPipeline(updatedSelectedPipeline);

    setNodes((ns): any =>
      ns.map((node: any) => {
        if (selectedNode?.id === node.id) {
          return createNode(
            selectedNode?.type,
            selectedNode.id,
            selectedNode?.position,
            { name: node.name, id: revision.id },
            getHandles("input", selectedNode.id, selectedNodeNewProcessEdges),
            getHandles("output", selectedNode.id, selectedNodeNewProcessEdges),
            selectedNodeInputTooltips,
            selectedNodeOutputTooltips
          );
        } else if (
          selectedNode.type === NODE_TYPES.ProcessNode &&
          Number(selectedNode.data?.processId) ===
            Number(node?.data?.processId) &&
          Number(node?.data?.processId) === Number(revision.id)
        ) {
          return createNode(
            node.type,
            node.id,
            node.position,
            { name: node.name, id: revision.id },
            getHandles("input", node.id, otherNodesNewProcessEdges),
            getHandles("output", node.id, otherNodesNewProcessEdges),
            getTooltips("input", node.id, otherNodesNewProcessEdges),
            getTooltips("output", node.id, otherNodesNewProcessEdges)
          );
        }
        return node;
      })
    );

    updateNodeInternals(id);

    if (closeDrawer) {
      setOpenDrawer(false);
    }
  };

  // The revision open in the drawer was deleted and the form loaded a surviving one. Point the
  // node at it and keep the drawer open on it. This used to happen through an overwrite save of
  // the deleted revision's draft, which deleted the survivor's ports.
  const handleRevisionDeleted = survivor => {
    const parsed = cleanAndParseProcess(survivor);
    setContentData(parsed);
    handleChangeRevision(parsed, { closeDrawer: false });
  };

  const handleCreateProcessNodeRender = (data, position, newGNum): void => {
    const gNumString = newGNum.toString();
    const edges = createNewProcessEdges(data, gNumString);
    const inputHandles = edges
      .filter(edge => edge.id.startsWith("i"))
      .map(edge => edge.id);

    const outputHandles = edges
      .filter(edge => edge.id.startsWith("o"))
      .map(edge => edge.id);
    const inputTooltips = data?.inputs?.map(
      ({ name, sname, qualifier, file_type }) => ({
        identifier: name,
        name: sname,
        qualifier,
        fileType: file_type,
      })
    );
    const outputTooltips = data?.outputs?.map(
      ({ name, sname, qualifier, file_type }) => ({
        identifier: name,
        name: sname,
        qualifier: qualifier,
        fileType: file_type,
      })
    );
    const metadata = { name: data?.name, id: data?.id };
    const node = createNode(
      NODE_TYPES.ProcessNode,
      gNumString,
      position,
      metadata,
      inputHandles,
      outputHandles,
      inputTooltips,
      outputTooltips
    );
    updateState(newGNum, node);
    const updatedSelectedPipeline = {
      ...selectedPipeline,
      [`process_${data.id}`]: data,
    } as PipelineInterface;
    setSelectedPipeline(updatedSelectedPipeline);
  };

  // Refactored onDrop function
  const onDrop = useCallback(
    event => {
      event.preventDefault();
      const render = event.dataTransfer.getData("dropData");
      const details = render.split("-");
      const type = details[0];
      const id = details[1];
      const newGNum = gNum + 1;
      const position = rfInstance?.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      switch (type) {
        case NODE_TYPES.InputNode:
        case NODE_TYPES.OutputNode:
          handleInputOutputNodeDnD(type, position, newGNum);
          break;
        case NODE_TYPES.ModuleNode:
          handleModuleNodeDnD(id, position, newGNum);
          break;
        case NODE_TYPES.ProcessNode:
          handleProcessNodeDnD(id, position, newGNum);
          break;
        case NODE_TYPES.ModelNode:
          handleModelNodeDnD(id, position, newGNum);
          break;
        case NODE_TYPES.SkillNode:
          handleSkillNodeDnD(id, position, newGNum);
          break;
      }
    },
    [rfInstance, gNum]
  );

  const onCreateProcess = useCallback(() => {
    const hydrated = createEmptyProcess();
    setContentData(hydrated);
    setDrawerContent(WorkflowEditTypes.EditProcess);
    setOpenDrawer(true);
  }, []);

  const onCreatePipeline = useCallback(() => {
    setIsCreatePipelineModalOpen(true);
  }, []);

  const onImportProcessFromGit = useCallback(() => {
    setIsImportProcessFromGitModalOpen(true);
  }, []);

  // To pass custom props from parent to custom nodes, we need to add these wrappers
  const inputNode = useCallback((props: NodeProps) => {
    return (
      <InputNode
        {...props}
        onDeleteInput={() => setIsDeleteNodeConfirmationModalOpen(true)}
        onEditInput={onEdit}
        onHandleHover={onHandleHover}
        onHandleLeave={onHandleLeave}
        isEditingAllowed={pipelineDraft?.isEditingAllowed}
      />
    );
  }, []);

  const outputNode = useCallback((props: NodeProps) => {
    return (
      <OutputNode
        {...props}
        onDeleteOutput={() => setIsDeleteNodeConfirmationModalOpen(true)}
        onEditOutput={onEdit}
        onHandleHover={onHandleHover}
        onHandleLeave={onHandleLeave}
        isEditingAllowed={pipelineDraft?.isEditingAllowed}
      />
    );
  }, []);

  const processNode = useCallback(
    (props: NodeProps) => {
      return (
        <ProcessNode
          {...props}
          onDelete={() => setIsDeleteNodeConfirmationModalOpen(true)}
          onEditProcess={onEdit}
          onEditDisplayName={onEditDisplayName}
          onHandleHover={onHandleHover}
          onHandleLeave={onHandleLeave}
          isEditingAllowed={pipelineDraft?.isEditingAllowed}
        />
      );
    },
    [selectedPipeline]
  );

  const moduleNode = useCallback(
    (props: NodeProps) => {
      return (
        <ModuleNode
          {...props}
          onDelete={() => setIsDeleteNodeConfirmationModalOpen(true)}
          onEditModule={onEdit}
          onEditDisplayName={onEditDisplayName}
          onModuleOpen={onModuleOpen}
          onHandleHover={onHandleHover}
          onHandleLeave={onHandleLeave}
          isEditingAllowed={pipelineDraft?.isEditingAllowed}
        />
      );
    },
    [selectedPipeline]
  );

  const modelNode = useCallback(
    (props: NodeProps) => {
      return (
        <ModelNode
          {...props}
          onDelete={() => setIsDeleteNodeConfirmationModalOpen(true)}
          onEditProcess={onEdit}
          onEditDisplayName={onEditDisplayName}
          onHandleHover={onHandleHover}
          onHandleLeave={onHandleLeave}
          isEditingAllowed={pipelineDraft?.isEditingAllowed}
        />
      );
    },
    [selectedPipeline]
  );

  const skillNode = useCallback(
    (props: NodeProps) => {
      return (
        <SkillNode
          {...props}
          onDelete={() => setIsDeleteNodeConfirmationModalOpen(true)}
          onEditProcess={onEdit}
          onEditDisplayName={onEditDisplayName}
          onHandleHover={onHandleHover}
          onHandleLeave={onHandleLeave}
          isEditingAllowed={pipelineDraft?.isEditingAllowed}
        />
      );
    },
    [selectedPipeline]
  );

  const drawers = useMemo(
    () => ({
      [WorkflowEditTypes.EditProcess]: (
        <CreateEditProcessForm
          data={contentData}
          title={
            contentData?.name === ""
              ? "Create New Process"
              : "Select or Create Revision"
          }
          isCreateProcessRevisionLoading={isCreateProcessRevisionLoading}
          isOverwriteRevisionLoading={isOverwriteRevisionLoading}
          isCreateNewProcessLoading={isCreateNewProcessLoading}
          onSaveProcess={handleSaveProcess}
          onChangeRevision={handleChangeRevision}
          onOverwriteProcess={handleOverwriteProcess}
          onCancel={onCancelDrawer}
          onDuplicateProcess={handleDuplicateProcess}
          onRevisionFetch={handleRevisionFetch}
          onRevisionDeleted={handleRevisionDeleted}
        />
      ),
    }),
    [
      contentData,
      drawerContent,
      modalContent,
      isOverwriteRevisionLoading,
      isCreateProcessRevisionLoading,
      isCreateNewProcessLoading,
      handleRevisionFetch,
    ]
  );

  const modals = useMemo(
    () => ({
      [WorkflowEditTypes.EditInput]: (
        <EditInputModal
          data={contentData}
          onSubmit={saveInputChange}
          onCancel={onCancelModal}
          isEditingAllowed={pipelineDraft?.isEditingAllowed}
        />
      ),
      [WorkflowEditTypes.EditOutput]: (
        <EditOutputModal
          data={contentData}
          onSubmit={saveOutputChange}
          onCancel={onCancelModal}
          isEditingAllowed={pipelineDraft?.isEditingAllowed}
        />
      ),
      [WorkflowEditTypes.EditModule]: (
        <EditModuleModal
          data={contentData}
          onCancel={onCancelModal}
          pipelineId={modulePipelineId}
          onChangeRevision={handleChangePipelineRevisionInModule}
        />
      ),
      [WorkflowEditTypes.EditDisplayName]: (
        <EditDisplayNameModal
          data={contentData}
          onSubmit={saveDisplayName}
          onCancel={onCancelModal}
        />
      ),
    }),
    [contentData, modalContent]
  );

  const nodeTypes = useMemo(
    () => ({
      [NODE_TYPES.InputNode]: inputNode,
      [NODE_TYPES.OutputNode]: outputNode,
      [NODE_TYPES.ProcessNode]: processNode,
      [NODE_TYPES.ModuleNode]: moduleNode,
      [NODE_TYPES.ModelNode]: modelNode,
      [NODE_TYPES.SkillNode]: skillNode,
    }),
    [selectedPipeline]
  );

  const handlePaneClick = () => {
    if (selectedNode) {
      setSelectedNode(null);
    }
    setEdges((edges): any =>
      edges.map((edge: EdgeProps) => {
        return {
          ...edge,
          style: {
            strokeWidth: 1,
          },
        };
      })
    );
  };

  return (
    <div className="flex h-[78dvh]">
      <Drawer
        id="pipepine-node-edit"
        open={openDrawer}
        setOpen={() => {}}
        size={DrawerSize.Big}>
        {drawerContent && drawers[drawerContent]}
      </Drawer>
      {isCreatePipelineModalOpen && (
        <CreatePipelineModal
          open={isCreatePipelineModalOpen}
          setOpen={setIsCreatePipelineModalOpen}
          menuGroups={menuGroups}
        />
      )}
      {isImportProcessFromGitModalOpen && (
        <ImportProcessFromGitModal
          open={isImportProcessFromGitModalOpen}
          setOpen={setIsImportProcessFromGitModalOpen}
        />
      )}
      {isDeleteNodeConfirmationModalOpen && (
        <DeleteNodeModal
          open={isDeleteNodeConfirmationModalOpen}
          setOpen={setIsDeleteNodeConfirmationModalOpen}
          onDelete={() => onDelete((selectedNode as NodeInterface)?.id)}
          nodeName={selectedNode?.name}
          nodeType={selectedNode?.type}
        />
      )}
      <Modal open={openModal} setOpen={setOpenModal} showButtons={false}>
        {modalContent && modals[modalContent]}
      </Modal>
      <PanelGroup direction="horizontal">
        <Panel defaultSize={20} minSize={16} maxSize={30}>
          {builderOptions && (
            <MemoizedSidebar
              options={builderOptions.data}
              onCreateProcess={onCreateProcess}
              onCreatePipeline={onCreatePipeline}
              onImportProcessFromGit={onImportProcessFromGit}
              onClickSidebarItem={onEdit}
              showMenu={pipelineDraft?.isEditingAllowed}
            />
          )}
        </Panel>
        <PanelResizeHandle className="w-2 cursor-col-resize border-r-[1px] border-gray-200 duration-150 ease-in-out hover:border-r-2 hover:border-gray-300" />
        <Panel minSize={30}>
          {isLoading ? (
            <div className="via-loading-spinner">
              <LogoSpinner />
            </div>
          ) : (
            <ReactFlow
              onlyRenderVisibleElements={true}
              ref={ref}
              onInit={setRfInstance}
              nodes={nodes}
              edges={edges}
              edgeTypes={edgeTypes}
              nodeTypes={nodeTypes}
              connectionLineType={ConnectionLineType.Straight}
              defaultEdgeOptions={defaultEdgeOptions}
              nodesDraggable={pipelineDraft?.isEditingAllowed}
              nodesConnectable={pipelineDraft?.isEditingAllowed}
              onConnect={onConnect}
              onConnectStart={onConnectStart}
              onConnectEnd={onConnectEnd}
              onDrop={onDrop}
              onDragOver={onDragOver}
              onPaneClick={handlePaneClick}
              onNodeClick={handleNodeClick}
              onNodeMouseEnter={onNodeMouseEnter}
              onNodeMouseLeave={onNodeMouseLeave}
              onNodeDragStart={handleNodeDragStart}
              onNodeDragStop={handleNodeDragStop}
              onEdgesChange={onEdgesChange as any}
              onNodesChange={onNodesChange as any}
              deleteKeyCode={null}
              nodeDragThreshold={1}
              panOnScroll={true}
              proOptions={proOptions}
              selectNodesOnDrag={false}
              zoomOnDoubleClick={true}
              attributionPosition="bottom-left"
              fitView
              fitViewOptions={fitViewOptions}
              minZoom={0.2}
              maxZoom={2}>
              {showMiniMap && (
                <MiniMap
                  ariaLabel="Pipeline Editor Minimap"
                  position="bottom-right"
                />
              )}
              <Controls
                position="top-right"
                showInteractive={pipelineDraft?.isEditingAllowed}>
                <MapPinIcon
                  onClick={() => setShowMiniMap(!showMiniMap)}
                  className="react-flow__controls-button h-6"
                />
              </Controls>
              <Background variant={BackgroundVariant.Dots} />
            </ReactFlow>
          )}
        </Panel>
      </PanelGroup>
      {isFetchProcessDetailsLoading && <OverlayLoading />}
    </div>
  );
}
